import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { ContentDailyCount } from '@/types/content';

// SPEC-021: Daily unique-viewer line for one content item. Mirrors the
// ActivityLineChart shape so it inherits the same theming and tooltip
// behavior as the rest of the analytics surface.

interface ContentTimeseriesChartProps {
  data: ContentDailyCount[];
}

const chartConfig: ChartConfig = {
  count: {
    label: 'Unique viewers',
    theme: { light: 'var(--chart-1)', dark: 'var(--chart-1)' },
  },
};

export function ContentTimeseriesChart({ data }: ContentTimeseriesChartProps) {
  const points = data.map((d) => ({ ...d, label: formatShortDay(d.day) }));
  return (
    <ChartContainer config={chartConfig} className="h-[240px] w-full">
      <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          fontSize={11}
          interval="preserveStartEnd"
          minTickGap={24}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          fontSize={11}
          allowDecimals={false}
        />
        <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
        <Area
          type="monotone"
          dataKey="count"
          stroke="var(--color-count)"
          fill="var(--color-count)"
          fillOpacity={0.35}
        />
      </AreaChart>
    </ChartContainer>
  );
}

// YYYY-MM-DD → "Apr 13". Kept local so this file doesn't depend on the
// other analytics modules' formatting helpers.
function formatShortDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return day;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
